import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, User, ChevronRight, Loader2, Mail, CalendarClock, Save } from "lucide-react";
import AppNav from "@/components/AppNav";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

const DAYS = [
  { value: 0, label: "Sunday" }, { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" }, { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" }, { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

const HOURS = Array.from({ length: 24 }, (_, h) => ({
  value: h,
  label: `${((h + 11) % 12) + 1}:00 ${h < 12 ? "AM" : "PM"}`,
}));

function getSupportedTimezones() {
  try {
    // Modern browsers support Intl.supportedValuesOf('timeZone')
    // eslint-disable-next-line no-undef
    return Intl.supportedValuesOf("timeZone");
  } catch {
    // Fallback: common IANA zones
    return [
      "UTC", "America/Los_Angeles", "America/Denver", "America/Chicago", "America/New_York",
      "America/Toronto", "America/Sao_Paulo", "Europe/London", "Europe/Berlin", "Europe/Paris",
      "Europe/Madrid", "Europe/Amsterdam", "Africa/Johannesburg", "Asia/Dubai", "Asia/Kolkata",
      "Asia/Bangkok", "Asia/Singapore", "Asia/Hong_Kong", "Asia/Tokyo", "Australia/Sydney",
      "Pacific/Auckland",
    ];
  }
}

function detectBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [students, setStudents] = useState(null);
  const [togglingDigest, setTogglingDigest] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [day, setDay] = useState(user?.digest_day ?? 0);
  const [hour, setHour] = useState(user?.digest_hour ?? 9);
  const [tz, setTz] = useState(user?.digest_timezone || "UTC");

  const timezoneList = useMemo(() => getSupportedTimezones(), []);

  // Sync local form state when the user object updates.
  useEffect(() => {
    if (!user) return;
    setDay(user.digest_day ?? 0);
    setHour(user.digest_hour ?? 9);
    setTz(user.digest_timezone || "UTC");
  }, [user?.digest_day, user?.digest_hour, user?.digest_timezone]); // eslint-disable-line

  // First-time-in-app timezone auto-detect: if the parent is on UTC (default) and
  // their browser reports a real IANA zone, offer to save it silently on first render.
  useEffect(() => {
    if (!user) return;
    const detected = detectBrowserTimezone();
    if (user.digest_timezone === "UTC" && detected && detected !== "UTC") {
      api.patch("/auth/me", { digest_timezone: detected }).then(refresh).catch(() => {});
    }
  }, [user?.id]); // eslint-disable-line

  const load = useCallback(async () => {
    try {
      const r = await api.get("/students");
      setStudents(r.data);
    } catch {
      setStudents([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const digestOn = user?.digest_enabled !== false;

  const toggleDigest = async () => {
    setTogglingDigest(true);
    try {
      await api.patch("/auth/me", { digest_enabled: !digestOn });
      await refresh();
      toast.success(digestOn ? "Weekly digest turned off." : "Weekly digest turned on.");
    } catch {
      toast.error("Could not update your preference.");
    } finally {
      setTogglingDigest(false);
    }
  };

  const saveSchedule = async () => {
    setSavingSchedule(true);
    try {
      await api.patch("/auth/me", {
        digest_day: Number(day),
        digest_hour: Number(hour),
        digest_timezone: tz,
      });
      await refresh();
      toast.success("Schedule saved.");
      setScheduleOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save schedule.");
    } finally {
      setSavingSchedule(false);
    }
  };

  const currentDay = DAYS.find((d) => d.value === (user?.digest_day ?? 0))?.label || "Sunday";
  const currentHour = HOURS.find((h) => h.value === (user?.digest_hour ?? 9))?.label || "9:00 AM";
  const currentTz = user?.digest_timezone || "UTC";

  return (
    <>
      <AppNav />
      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-10" data-testid="dashboard-page">
        <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
          <div>
            <p className="tm-chip mb-3">Signed in as {user?.role}</p>
            <h1 className="text-3xl sm:text-4xl font-black" style={{ fontFamily: "Nunito, sans-serif" }}>
              Hi {user?.name?.split(" ")[0] || "there"}
            </h1>
            <p className="text-[color:var(--tm-ink-3)] mt-1">Choose a student to open their space.</p>
          </div>
          <Link to="/onboarding" className="tm-btn tm-btn-primary" data-testid="dashboard-add-student-btn">
            <Plus size={18} /> Add student
          </Link>
        </div>

        {/* Weekly digest preference */}
        <section className="tm-card-soft mb-8" data-testid="digest-preference-section">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div
              className="p-3 rounded-xl flex-shrink-0"
              style={{ background: "#ffffff", color: "var(--tm-brand)", border: "2px solid var(--tm-border)" }}
            >
              <CalendarClock size={22} />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-bold" style={{ fontFamily: "Nunito, sans-serif" }}>
                Weekly digest email
              </h2>
              <p className="text-[color:var(--tm-ink-3)]" data-testid="digest-schedule-summary">
                {digestOn
                  ? `Every ${currentDay} at ${currentHour} in ${currentTz}.`
                  : "You are opted out — turn it back on any time."}
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {digestOn && (
                <button
                  onClick={() => setScheduleOpen((v) => !v)}
                  className="tm-btn tm-btn-outline"
                  data-testid="digest-schedule-toggle-btn"
                  aria-expanded={scheduleOpen}
                >
                  {scheduleOpen ? "Close" : "Change schedule"}
                </button>
              )}
              <button
                onClick={toggleDigest}
                disabled={togglingDigest}
                role="switch"
                aria-checked={digestOn}
                className={`tm-btn ${digestOn ? "tm-btn-primary" : "tm-btn-outline"}`}
                data-testid="digest-preference-toggle"
              >
                {togglingDigest ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
                {digestOn ? "Auto-send is on" : "Auto-send is off"}
              </button>
            </div>
          </div>

          {digestOn && scheduleOpen && (
            <div
              className="mt-5 pt-5 border-t-2 grid sm:grid-cols-3 gap-4"
              style={{ borderColor: "var(--tm-border)" }}
              data-testid="digest-schedule-form"
            >
              <div>
                <label htmlFor="digest-day" className="tm-label">Day of week</label>
                <select
                  id="digest-day"
                  className="tm-input"
                  value={day}
                  onChange={(e) => setDay(e.target.value)}
                  data-testid="digest-day-select"
                >
                  {DAYS.map((d) => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="digest-hour" className="tm-label">Local time</label>
                <select
                  id="digest-hour"
                  className="tm-input"
                  value={hour}
                  onChange={(e) => setHour(e.target.value)}
                  data-testid="digest-hour-select"
                >
                  {HOURS.map((h) => (
                    <option key={h.value} value={h.value}>{h.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="digest-tz" className="tm-label">Timezone</label>
                <select
                  id="digest-tz"
                  className="tm-input"
                  value={tz}
                  onChange={(e) => setTz(e.target.value)}
                  data-testid="digest-timezone-select"
                >
                  {timezoneList.map((z) => (
                    <option key={z} value={z}>{z}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-3 flex justify-end">
                <button
                  onClick={saveSchedule}
                  disabled={savingSchedule}
                  className="tm-btn tm-btn-primary"
                  data-testid="digest-schedule-save-btn"
                >
                  {savingSchedule ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                  Save schedule
                </button>
              </div>
            </div>
          )}
        </section>

        {students === null ? (
          <div className="tm-card flex items-center gap-3 text-[color:var(--tm-ink-3)]">
            <Loader2 className="animate-spin" size={18} /> Loading…
          </div>
        ) : students.length === 0 ? (
          <div className="tm-card text-center py-14">
            <div
              className="mx-auto mb-4 rounded-full grid place-items-center"
              style={{ width: 64, height: 64, background: "var(--tm-bg-3)", color: "var(--tm-brand)" }}
            >
              <User size={30} />
            </div>
            <h2 className="text-xl font-bold mb-2">No students yet</h2>
            <p className="text-[color:var(--tm-ink-3)] mb-5">Add your first student to get started.</p>
            <Link to="/onboarding" className="tm-btn tm-btn-primary" data-testid="dashboard-empty-cta">
              <Plus size={18} /> Add student
            </Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="dashboard-students-grid">
            {students.map((s) => (
              <Link
                key={s.id}
                to={`/students/${s.id}`}
                className="tm-card block hover:border-[color:var(--tm-brand)] transition-colors"
                data-testid={`student-card-${s.id}`}
              >
                <div className="flex items-center gap-4 mb-3">
                  <div
                    className="rounded-full grid place-items-center text-white font-black text-xl"
                    style={{
                      width: 56,
                      height: 56,
                      background: s.avatar_color || "var(--tm-brand)",
                      fontFamily: "Nunito, sans-serif",
                    }}
                    aria-hidden
                  >
                    {s.name?.[0]?.toUpperCase() || "S"}
                  </div>
                  <div>
                    <p className="text-lg font-bold">{s.name}</p>
                    <p className="text-sm text-[color:var(--tm-ink-3)]">Age {s.age} · {s.reading_level || "—"}</p>
                  </div>
                </div>
                {s.interests?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {s.interests.slice(0, 4).map((i) => (
                      <span key={i} className="tm-chip">{i}</span>
                    ))}
                  </div>
                )}
                <div className="flex items-center justify-end mt-4 text-sm font-bold" style={{ color: "var(--tm-brand)" }}>
                  Open <ChevronRight size={16} />
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </>
  );
}
